import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'

import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'

import { useJoinWaitingRoomConsultationMutation } from '@/core/services/waiting-room'
import { WaitingRoom, WaitingRoomActiveConsultation } from '@/core/models/waiting-room.model'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { getApiErrorMessage } from '@/utils/errors'

import { useDoctorsLoader } from '../model/useDoctorsLoader'
import { DoctorsTable } from './DoctorsTable'
import { InProgressConsultations } from './InProgressConsultations'

export function DoctorsTab({
  activeConsultations,
  waitingRoom,
  waitingRoomId,
  onPreviewConsultation,
}: {
  activeConsultations: WaitingRoomActiveConsultation[]
  waitingRoom: WaitingRoom
  waitingRoomId: string
  // SEHA (en-SA) read-only case preview; when omitted the tab renders exactly as before
  onPreviewConsultation?: (consultationId: string) => void
}) {
  const data = useDoctorsLoader(waitingRoomId)
  const authUser = useAppSelector(selectAuthUser)
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const [joinConsultation, { isLoading: isJoining }] = useJoinWaitingRoomConsultationMutation()
  const [joiningConsultationId, setJoiningConsultationId] = useState<string | null>(null)

  const openConsultation = (consultation: WaitingRoomActiveConsultation) => {
    window.open(`/consultations/${consultation._id}/${consultation.isGroup ? 'group-live' : 'live'}`, '_blank')
  }

  const handleJoin = async (consultation: WaitingRoomActiveConsultation, assign: boolean) => {
    setJoiningConsultationId(consultation._id)
    try {
      await joinConsultation({ waitingRoomId, consultationId: consultation._id, assign }).unwrap()
      openConsultation(consultation)
    } catch (error) {
      const code = (error as { data?: { code?: string; retryInSeconds?: number } })?.data?.code
      const seconds = (error as { data?: { retryInSeconds?: number } })?.data?.retryInSeconds ?? 0
      const codeMessages: Record<string, string> = {
        'waitingRoom.coPresenceDisabled': t('notification.waitingRoom.coPresence.disabled'),
        'waitingRoom.coPresenceAssignDisabled': t('notification.waitingRoom.coPresence.assignDisabled'),
        'waitingRoom.notMember': t('notification.waitingRoom.acceptConsultation.notMember'),
        'waitingRoom.notActive': t('notification.waitingRoom.coPresence.notActive'),
        'waitingRoom.capReached': t('notification.waitingRoom.acceptConsultation.capReached'),
        'waitingRoom.cooldown': t('notification.waitingRoom.acceptConsultation.cooldown', { seconds }),
      }
      const message = (code && codeMessages[code]) || getApiErrorMessage(error, t('notification.waitingRoom.coPresence.error'))
      enqueueSnackbar(message, { variant: 'error' })
    } finally {
      setJoiningConsultationId(null)
    }
  }

  return (
    <Stack>
      <InProgressConsultations
        authUser={authUser}
        consultations={activeConsultations}
        waitingRoom={waitingRoom}
        isJoining={isJoining}
        joiningConsultationId={joiningConsultationId}
        onJoin={handleJoin}
        onRejoin={openConsultation}
        onPreview={onPreviewConsultation ? (consultation) => onPreviewConsultation(consultation._id) : undefined}
      />
      {!!activeConsultations.length && <Divider sx={{ mt: 2 }} />}
      <DoctorsTable data={data} />
    </Stack>
  )
}
